import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchVendorById, getCurrentUser } from '../services/api';
import { Visibility, Edit } from '@mui/icons-material';

const VendorProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const isOwnProfile = currentUser?.id === parseInt(id);
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Helper function to parse working hours string
  const parseWorkingHours = (hoursString) => {
    if (!hoursString) return { days: [], startTime: '', endTime: '' };
    
    try {
      // Format: "Mon, Tue, Wed, Thu, Fri (09:00-18:00)"
      const match = hoursString.match(/(.+?)\s*\((\d{2}:\d{2})-(\d{2}:\d{2})\)/);
      if (match) {
        const daysStr = match[1];
        const start = match[2];
        const end = match[3];
        const days = daysStr.split(',').map(d => d.trim());
        return { days, startTime: start, endTime: end };
      }
    } catch (err) {
      console.error('Error parsing hours:', err);
    }
    return { days: [], startTime: '', endTime: '' };
  };

  useEffect(() => {
    if (id) loadVendor();
  }, [id]);

  const loadVendor = async () => {
    try {
      setLoading(true);
      const data = await fetchVendorById(id || 1);
      setVendor(data);
    } catch (err) {
      setError('Failed to load vendor information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div style={S.loader}>Loading Professional Profile...</div>;
  if (error || !vendor) return <div style={S.error}>{error || 'Vendor not found'}</div>;

  return (
    <div style={S.pageWrapper}>
      <style>{`
        .action-btn:hover { transform: translateY(-2px); filter: brightness(1.1); }
        .service-item:hover { border-color: #6366f1 !important; background: #f8fafc !important; }
        @media (max-width: 768px) {
          .main-grid { grid-template-columns: 1fr !important; }
          .sticky-sidebar { position: static !important; width: 100% !important; }
          .profile-header-content { text-align: center; flex-direction: column; }
        }
      `}</style>

      {/* ── Banner / Header Section ── */}
      <div style={S.headerBanner}>
        <div style={S.container}>
          <div className="profile-header-content" style={S.headerFlex}>
            <div style={S.avatarLarge}>
              {vendor.name?.[0] || 'V'}
              {vendor.is_verified && <div style={S.verifiedBadge}>✓</div>}
            </div>
            
            <div style={S.headerInfo}>
              <h1 style={S.vendorName}>{vendor.name}</h1>
              <div style={S.tagline}>
                <span style={S.categoryTag}>{vendor.services_provided || 'Expert Partner'}</span>
                <span style={S.locationTag}>📍 {vendor.area?.name || 'Local'}</span>
                <span style={S.locationTag}>, {vendor.city?.name || 'Local'}</span>
              </div>
              <div style={S.statsRow}>
                <div style={S.statItem}><strong>★ {vendor.rating?.toFixed(1)}</strong> Ratings</div>
                <div style={S.statDivider} />
                <div style={S.statItem}><strong>{vendor.reviews?.length || 0}</strong> Reviews</div>
              </div>
            </div>

            <div style={S.headerActions}>
              {isOwnProfile && (
                <button
                  onClick={() => navigate('/vendor-edit-profile')}
                  className="action-btn"
                  style={{ ...S.btnCall, background: '#6366f1', color: 'white' }}
                >
                  <Edit style={{ marginRight: '4px', fontSize: '18px' }} /> Edit Profile
                </button>
              )}
              <a href={`tel:${vendor.phone}`} className="action-btn" style={S.btnCall}>
                📞 Call Now
              </a>
              <a href={`https://wa.me/${vendor?.whatsapp?.replace(/\D/g, '')}`} className="action-btn" style={S.btnWA}>
                💬 WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Content Grid ── */}
      <div className="main-grid" style={S.mainGrid}>
        <div style={S.leftCol}>
          <section style={S.section}>
            <h3 style={S.sectionTitle}>About the Professional</h3>
            <p style={S.aboutText}>{vendor.about || vendor.description || 'Dedicated professional providing top-tier services in your area with a focus on quality and reliability.'}</p>
          </section>

          <section style={S.section}>
            <h3 style={S.sectionTitle}>Services & Pricing</h3>
            <div style={S.serviceList}>
              {vendor.services?.map((svc) => (
                <div key={svc.id} className="service-item" style={S.serviceCard}>
                  <div style={S.svcLeft}>
                    <div style={S.svcIcon}>🔹</div>
                    <div>
                      <div style={S.svcName}>{svc.name}</div>
                      <div style={S.svcCat}>{svc.category?.name}</div>
                    </div>
                  </div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
                    <div style={S.svcPrice}>₹{svc.price}</div>
                    <button
                      onClick={() => navigate(`/service/${svc.id}`)}
                      style={{
                        background: '#f1f5f9',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '8px 10px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#6366f1',
                        transition: '0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.background = '#e2e8f0'}
                      onMouseLeave={(e) => e.target.style.background = '#f1f5f9'}
                    >
                      <Visibility fontSize="small" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        {/* ── Sticky Sidebar ── */}
        <div className="sticky-sidebar" style={S.sidebar}>
          <div style={S.bookingCard}>
            <h3 style={S.sideTitle}>Working Details</h3>
            
            {vendor.working_hours && (
              <>
                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>Working Days</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {parseWorkingHours(vendor.working_hours).days.map((day, idx) => (
                      <div key={idx} style={{ padding: '6px 12px', background: '#6366f1', color: 'white', borderRadius: '6px', fontSize: '12px', fontWeight: '700' }}>
                        {day}
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ marginBottom: '20px' }}>
                  <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' }}>Working Hours</div>
                  <div style={{ fontSize: '15px', fontWeight: '700', color: '#1e293b' }}>
                    {parseWorkingHours(vendor.working_hours).startTime} - {parseWorkingHours(vendor.working_hours).endTime}
                  </div>
                </div>
              </>
            )}
            
            <div style={S.sideDetail}>
              <span>Address</span>
              <strong>{vendor.address || 'Standard Service Area'}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── UI Design Tokens ── */
const S = {
  pageWrapper: { background: '#f8fafc', minHeight: '100vh', fontFamily: "'Nunito', sans-serif" },
  container: { maxWidth: '1100px', margin: '0 auto', padding: '0 20px' },
  
  headerBanner: { 
    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', 
    padding: '60px 0', 
    color: '#fff',
    borderBottom: '4px solid #6366f1'
  },
  
  headerFlex: { display: 'flex', alignItems: 'center', gap: '30px', position: 'relative' },
  
  avatarLarge: {
    width: '100px', height: '100px', borderRadius: '24px', background: '#6366f1',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '40px', fontWeight: '800', color: '#fff', position: 'relative',
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)', border: '4px solid rgba(255,255,255,0.1)'
  },

  verifiedBadge: {
    position: 'absolute', bottom: '-5px', right: '-5px', background: '#10b981',
    width: '24px', height: '24px', borderRadius: '50%', fontSize: '12px',
    display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #1e293b'
  },

  headerInfo: { flex: 1 },
  vendorName: { fontSize: '32px', fontWeight: '900', margin: '0 0 8px 0', letterSpacing: '-0.5px' },
  
  tagline: { display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' },
  categoryTag: { background: 'rgba(255,255,255,0.1)', padding: '4px 12px', borderRadius: '50px', fontSize: '12px', fontWeight: '700' },
  locationTag: { fontSize: '14px', color: '#cbd5e1' },

  statsRow: { display: 'flex', alignItems: 'center', gap: '15px' },
  statItem: { fontSize: '14px', color: '#94a3b8' },
  statDivider: { width: '1px', height: '14px', background: 'rgba(255,255,255,0.2)' },

  headerActions: { display: 'flex', gap: '12px' },
  
  btnCall: { 
    background: '#fff', color: '#1e293b', padding: '12px 20px', borderRadius: '12px', 
    fontWeight: '800', textDecoration: 'none', transition: '0.2s', fontSize: '14px' 
  },
  btnWA: { 
    background: '#22c55e', color: '#fff', padding: '12px 20px', borderRadius: '12px', 
    fontWeight: '800', textDecoration: 'none', transition: '0.2s', fontSize: '14px' 
  },

  mainGrid: { 
    maxWidth: '1100px', margin: '30px auto', padding: '0 20px',
    display: 'grid', gridTemplateColumns: '1fr 340px', gap: '30px' 
  },

  section: { background: '#fff', padding: '24px', borderRadius: '16px', marginBottom: '24px', border: '1px solid #e2e8f0' },
  sectionTitle: { fontSize: '18px', fontWeight: '800', color: '#0f172a', marginBottom: '16px' },
  aboutText: { color: '#475569', lineHeight: '1.7', fontSize: '15px' },

  serviceCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '16px', borderRadius: '12px', border: '1px solid #f1f5f9',
    marginBottom: '10px', transition: '0.2s'
  },
  svcLeft: { display: 'flex', gap: '12px', alignItems: 'center' },
  svcName: { fontWeight: '700', color: '#1e293b', fontSize: '15px' },
  svcCat: { fontSize: '12px', color: '#64748b' },
  svcPrice: { fontWeight: '900', color: '#6366f1', fontSize: '16px' },

  sidebar: { position: 'sticky', top: '100px' },
  bookingCard: { 
    background: '#fff', padding: '24px', borderRadius: '16px', 
    border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' 
  },
  sideTitle: { fontSize: '16px', fontWeight: '800', marginBottom: '20px' },
  sideDetail: { display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px', fontSize: '14px' },
  secureNote: { textAlign: 'center', fontSize: '11px', color: '#94a3b8', marginTop: '12px' },
  
  loader: { padding: '100px', textAlign: 'center', fontSize: '18px', fontWeight: '700', color: '#64748b' }
};

export default VendorProfile;